import React, { useCallback, useEffect, useState } from 'react';
import { Box, Typography, IconButton, Paper, TableContainer, CircularProgress } from '@mui/material';
import { Delete, Edit } from '@mui/icons-material';
import CustomButton from '../../../Components/CustomButton';
import CommonTable from '../../../Components/shared/CommonTable/CommonTable';
import { createNewSupplier, getSuppliers, deleteSupplier, updateSupplier, getSupplierById } from '../../../redux/slices/supplierSlice';
import { getContactById } from '../../../redux/slices/contactSlice';
import { connect, useSelector } from 'react-redux';
import ModalForms from '../../../Components/Simple Purchase/ModalForms';
import ConfirmDeleteModal from '../../../Components/shared/Modals/ConfirmDeleteModal';
import { supplierInitialValues, supplierValidationSchema } from '../../../formSchemas/supplierSchema';

const SupplierListing = ({ getSupplierList, createSupplier, deleteSupplier, getSuppliersById, updateSupplierById, getContactsById }) => {
  const { suppliersListLoading, supplierLoading, deleteSupplierLoading } = useSelector((state) => state.supplier);

  const [suppliers, setSuppliers] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [modalFormData, setModalFormData] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedSupplierId, setSelectedSupplierId] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [formInitialValues, setFormInitialValues] = useState(supplierInitialValues);

  const handleAccordionOpen = (row) => {
    if (row.contacts && row.contacts.length > 0) {
      row.contacts.forEach(contact => {
        const href = contact.href;
        const contactId = href.split('/').pop();
        if (contactId) {
          getContactsById({ id: contactId });
        }
      });
    }
  };

  const generateModalFormData = () => [
    { key: "name", label: "Name", type: "text", required: true, width: { xs: '100%', md: "49%" }, placeholder: "Enter supplier name" },
    { key: "status", label: "Status", type: "select", required: true, width: { xs: '100%', md: "49%" }, placeholder: "Select...", data: ["Active", "Inactive"] },
    { key: "currency", label: "Currency", type: "select", required: true, width: { xs: '100%', md: "49%" }, placeholder: "Select...", data: ["INR", "USD", "CAD"] },
    { key: "payment_terms", label: "Payment Term", type: "select", required: true, width: { xs: '100%', md: "49%" }, placeholder: "Select...", data: [30, 60, 90] },
    { key: "attribute_set", label: "Attribute Set", type: "select", width: { xs: '100%', md: "49%" }, placeholder: "Select...", data: ["Default", "Custom"] },
    { key: "tax_rule", label: "Tax Rule", type: "select", required: true, width: { xs: '100%', md: "49%" }, placeholder: "Select...", data: ['Tax Exempt', 'GST', 'PST', 'PST/GST', 'HST'] },
    { key: "default_carrier", label: "Default Carrier", type: "select", width: { xs: '100%', md: "49%" }, placeholder: "Select...", data: ["Canada Post", "FedEx", "UPS"] },
    { key: "discount", label: "Discount", type: "number", width: { xs: '100%', md: "49%" }, placeholder: "Enter Discount" },
    { key: "tax_number", label: "Tax Number", type: "text", width: { xs: '100%', md: "49%" }, placeholder: "Enter Tax Number" },
    { key: "comments", label: "Comments", type: "textField", width: "100%", placeholder: "Enter Comments" },
  ];

  const openAddSupplierModal = () => {
    setEditMode(false);
    setFormInitialValues(supplierInitialValues);
    setModalFormData(generateModalFormData());
    setOpenModal(true);
  };

  const mapSupplierDataToInitialValues = (supplier) => ({
    name: supplier.name || "",
    type: supplier.type || "Supplier",
    comments: supplier.comments || "",
    payment_terms: Number(supplier?.payment_terms),
    discount: Number(supplier?.discount) || 0,
    tax_rule: supplier.tax_rule || "",
    currency: supplier.currency || "",
    status: supplier.status || "",
    tax_number: supplier.tax_number || "",
    default_carrier: supplier.default_carrier || "",
    fax: supplier.fax || "",
    website: supplier.website || "",
    contacts: supplier.contacts || [],
    addresses: supplier.addresses || [],
    products: supplier.products || []
  });

  const handleEditSupplier = async (id) => {
    const res = await getSuppliersById({ id });
    const supplier = res?.payload?.data[0];

    if (supplier) {
      const mappedValues = mapSupplierDataToInitialValues(supplier);
      setFormInitialValues(mappedValues);
      setSelectedSupplierId(id);
      setEditMode(true);
      setModalFormData(generateModalFormData());
      setOpenModal(true);
    }
  };
  
  const handleCreateSupplier = async (values) => {
    const res = await createSupplier(values);
    if (createNewSupplier.fulfilled.match(res)) {
      setOpenModal(false);
      fetchData();
    }
  };

  const handleUpdateSupplier = async (values) => {
    const res = await updateSupplierById({ id: selectedSupplierId,  ...values });
    // const res = await updateContactById({ id: selectedContactId, ...values });
    if (updateSupplier.fulfilled.match(res)) {
      setOpenModal(false);
      fetchData();
    }
  };

  const openDeleteModal = (id) => {
    setSelectedSupplierId(id);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirmed = async () => {
    await deleteSupplier({ id: selectedSupplierId });
    setShowDeleteModal(false);
    fetchData();
  };

  const fetchData = useCallback(async () => {
    const res = await getSupplierList();
    setSuppliers(res?.payload?.data || []);
  }, [getSupplierList]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const columns = [
    { key: 'name', label: 'Supplier Name' },
    { key: 'type', label: 'Type' },
    { key: 'tax_rule', label: 'Tax Rule' },
    { key: 'patment_terms', label: 'Payment Terms (Days)' }
  ];

  const renderActions = (row) => (
    <>
      <IconButton onClick={() => handleEditSupplier(row.id)}>
        <Edit />
      </IconButton>
      <IconButton color="error" onClick={() => openDeleteModal(row.id)}>
        <Delete />
      </IconButton>
    </>
  );

return (
    <Box sx={{ paddingX: 0 }}>
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        backgroundColor: '#fff',
        paddingY: { xs: 1, md: 2 },
        paddingX: { xs: 1.5, md: 3 },
        borderRadius: 2,
        mt: 1,
        flexWrap: 'wrap',
        rowGap: 1,
        boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
        border: '1px solid #e0e0e0'
      }}>
        <Typography
          variant="h5"
          fontWeight={600}
          sx={{ fontSize: { xs: '1.2rem', md: '1.5rem' }, textWrap: "nowrap" }}
        >
          Suppliers
        </Typography>
        <CustomButton
          text="Add Supplier"
          bgcolor="#95ABEB"
          color="black"
          sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' }, padding: { xs: '4px 10px', md: '6px 16px' } }}
          onClick={openAddSupplierModal}
        />
      </Box>

      {suppliersListLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}>
          <CircularProgress />
        </Box>
      ) : suppliers.length === 0 ? (
        <Box sx={{ textAlign: 'center', mt: 5 }}>
          <Typography variant="h6" color="text.secondary" sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }}>
            No suppliers found
          </Typography>
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{
          mt: 2,
          overflowX: 'auto',
          borderRadius: 2,
          boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
          border: '1px solid #e0e0e0',
          '& thead': {
            backgroundColor: '#f9fafb',
            '& th': {
              fontWeight: 600,
              fontSize: { xs: '0.75rem', md: '0.875rem' },
              textWrap: "nowrap",
              color: '#333'
            }
          }
        }}>
          <CommonTable columns={columns} data={suppliers} renderActions={renderActions} onAccordianOpen={handleAccordionOpen} />
        </TableContainer>
      )}

      <ConfirmDeleteModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        handleDelete={handleDeleteConfirmed}
        isLoading={deleteSupplierLoading}
        heading="Delete Supplier"
        description="Are you sure you want to delete this supplier? This action cannot be undone."
      />

      <ModalForms
        open={openModal}
        setOpen={setOpenModal}
        name={editMode ? "Edit Supplier" : "Add Supplier"}
        handleSave={editMode ? handleUpdateSupplier : handleCreateSupplier}
        isLoading={supplierLoading}
        data={modalFormData}
        initialValues={formInitialValues}
        validationSchema={supplierValidationSchema}
      />
    </Box>
  );
};

const mapDispatchToProps = (dispatch) => ({
  getSupplierList: (data) => dispatch(getSuppliers(data)),
  createSupplier: (data) => dispatch(createNewSupplier(data)),
  deleteSupplier: (data) => dispatch(deleteSupplier(data)),
  getSuppliersById: (data) => dispatch(getSupplierById(data)),
  getContactsById: (data) => dispatch(getContactById(data)),
  updateSupplierById: (data) => dispatch(updateSupplier(data)),
});

export default connect(null, mapDispatchToProps)(SupplierListing);
